// Card / Panel: token-styled surfaces. Card adds an optional title/actions
// header; Panel is the headerless surface for arbitrary grouping. Both read
// their fill, stroke, and radius from --dsw-* tokens.

import type { HTMLAttributes, ReactNode } from 'react'
import clsx from 'clsx'
import css from './Card.module.css'

export type CardProps = {
  /** Optional header title (rendered as an h2). */
  title?: ReactNode
  /** Optional header trailing slot (buttons, badges). */
  actions?: ReactNode
  className?: string
  /** Extra class for the body region. */
  bodyClassName?: string
  children?: ReactNode
} & Omit<HTMLAttributes<HTMLElement>, 'title'>

/**
 * Render a titled surface.
 * @param props - optional title/actions header, body children, native attributes.
 * @returns the card element.
 */
export function Card({ title, actions, className, bodyClassName, children, ...rest }: CardProps) {
  const hasHeader = title != null || actions != null
  return (
    <section className={clsx(css.card, className)} {...rest}>
      {hasHeader && (
        <header className={css.header}>
          {title != null && <h2 className={css.title}>{title}</h2>}
          {actions != null && <div className={css.actions}>{actions}</div>}
        </header>
      )}
      <div className={clsx(css.body, bodyClassName)}>{children}</div>
    </section>
  )
}

export type PanelProps = {
  /** Body inset scale. */
  padding?: 'none' | 'sm' | 'md'
  className?: string
  children?: ReactNode
} & HTMLAttributes<HTMLDivElement>

/**
 * Render a headerless surface.
 * @param props - padding scale, children, native div attributes.
 * @returns the panel element.
 */
export function Panel({ padding = 'md', className, children, ...rest }: PanelProps) {
  return (
    <div className={clsx(css.panel, css[`pad-${padding}`], className)} {...rest}>
      {children}
    </div>
  )
}
